import { useRef, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface EditableProps {
  columnKey: string;
  rowId: string;
  isEditing: boolean;
}

const NOTHING_EDITING: EditableProps = {
  isEditing: false,
  columnKey: '',
  rowId: '',
};

export interface UseTableProps {
  onSort?: (key: string, direction: SortDirection) => void;
  onEditSubmit?: (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => void;
}

export function useTable({ onSort, onEditSubmit }: UseTableProps = {}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [editing, setEditing] = useState<EditableProps>(NOTHING_EDITING);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  // A save resolving late reports done through the closure it was opened with,
  // so the guard has to read the cell being edited now, not the one captured then.
  const editingRef = useRef(editing);

  const startEditing = (next: EditableProps) => {
    editingRef.current = next;
    setEditing(next);
  };

  const handleSort = (columnKey: string) => {
    let newDirection: SortDirection = 'asc';

    if (sortKey === columnKey) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      } else {
        newDirection = 'asc';
      }
    }
    setSortKey(newDirection ? columnKey : null);
    setSortDirection(newDirection);
    onSort?.(columnKey, newDirection);
  };

  const handleEditContinue = (value: string) => {
    setIsSubmittingEdit(true);
    onEditSubmit?.(editing.rowId, editing.columnKey, value);
  };

  const handleEditClick = (columnKey: string, rowId: string) => {
    startEditing({ isEditing: true, columnKey, rowId });
    setIsSubmittingEdit(false);
  };

  const handleEditDone = () => {
    startEditing(NOTHING_EDITING);
    setIsSubmittingEdit(false);
  };

  const handleEditDoneFor = (columnKey: string, rowId: string) => () => {
    const current = editingRef.current;
    if (current.columnKey !== columnKey || current.rowId !== rowId) return;
    handleEditDone();
  };

  return {
    sortKey,
    sortDirection,
    editing,
    isSubmittingEdit,
    handleSort,
    handleEditContinue,
    handleEditClick,
    handleEditDone,
    handleEditDoneFor,
  };
}
